/**
 * VÉRAULT Portal — Advisor: Clients list (the entry point to the lighthouse).
 * Calm editorial table. Each row navigates to the lighthouse Client Profile.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Users, UserPlus } from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import NewClientModal from "../../components/NewClientModal";

const { fadeInUp, stagger } = portalMotion;

export default function AdvisorClients() {
  const [clients, setClients] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [newOpen, setNewOpen] = React.useState(false);

  const fetchClients = React.useCallback(() => {
    api
      .get("/travels/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setError("Couldn't load clients."));
  }, []);

  React.useEffect(() => {
    fetchClients();
    // Wave 26.18 — refetch when a client is deleted/restored anywhere
    const handler = () => fetchClients();
    window.addEventListener("verault:clients-changed", handler);
    return () => window.removeEventListener("verault:clients-changed", handler);
  }, [fetchClients]);

  const filtered = React.useMemo(() => {
    if (!clients) return null;
    if (!q.trim()) return clients;
    const needle = q.toLowerCase();
    return clients.filter((c) =>
      [c.full_name, c.email, c.phone, c.household_label]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [clients, q]);

  // Quick lookup: client id → full_name (so household members can show their head)
  const nameById = React.useMemo(() => {
    const m = {};
    (clients || []).forEach((c) => {
      if (c.id) m[c.id] = c.full_name || "Unnamed";
    });
    return m;
  }, [clients]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Clients"
          title="Your clients"
          lede="The households and travelers under your care."
          actions={
            <PortalButton
              variant="primary"
              size="sm"
              icon={<UserPlus className="size-3.5" strokeWidth={1.5} />}
              onClick={() => setNewOpen(true)}
              data-testid="clients-new-client-btn"
            >
              New client
            </PortalButton>
          }
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <div className="flex items-center gap-2 max-w-md rounded-full bg-white/60 border border-[rgba(0,0,0,0.06)] px-4 h-10">
          <Search className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, email, phone…"
            data-testid="clients-filter"
            className="flex-1 bg-transparent font-body text-[14px] focus:outline-none placeholder-[hsl(var(--foreground-muted))]"
          />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={filtered ? `${filtered.length} on file` : "loading"} />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<Users className="size-6" strokeWidth={1.25} />} />
          ) : !filtered ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty title="No clients match that filter" icon={<Users className="size-6" strokeWidth={1.25} />} />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {filtered.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/portal/advisor/clients/${c.id}`}
                      className="group flex items-center justify-between gap-4 px-5 sm:px-6 py-4 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
                      data-testid={`client-row-${c.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-lg text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
                          {c.full_name || "Unnamed"}
                        </p>
                        {c.household_client_id && nameById[c.household_client_id] && (
                          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                            Member · {nameById[c.household_client_id]}
                          </p>
                        )}
                        <p className="mt-0.5 font-body text-[13px] text-[hsl(var(--foreground-secondary))] truncate">
                          {[c.email, c.phone].filter(Boolean).join(" · ") || "No contact on file"}
                        </p>
                      </div>
                      <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                        <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                          {c.trip_count || 0} trip{(c.trip_count || 0) === 1 ? "" : "s"}
                        </span>
                        {c.balance_due > 0 && (
                          <span className="font-mono text-[11px] text-[hsl(var(--accent-terracotta))]">
                            {fmt.money(c.balance_due)} due
                          </span>
                        )}
                        {c.consultation_status === "scheduled" && (
                          <span
                            className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]"
                            data-testid={`client-consult-${c.id}`}
                          >
                            Consultation booked
                          </span>
                        )}
                      </div>
                      <StatusTag tone={c.consultation_status === "scheduled" ? "gold" : c.status === "active" ? "sage" : "neutral"}>
                        {c.consultation_status === "scheduled" ? "lead" : (c.status || "active")}
                      </StatusTag>
                    </Link>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>

      <NewClientModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => fetchClients()}
        navigateToProfile
      />
    </motion.div>
  );
}
